/**
 * API Error Handler
 */

export interface APIError {
  status?: number;
  code?: string;
  message?: string;
  messageKey?: string;  // i18n key for the message
  data?: any;
}

export class APIErrorHandler {
  private static creditModalCallback: (() => void) | null = null;

  /**
   * Set credit modal callback
   */
  static setCreditModalCallback(callback: () => void): void {
    this.creditModalCallback = callback;
  }

  /**
   * Handle API error
   */
  static handleError(error: any): APIError {
    console.error('API Error:', error);

    // Network error
    if (!error.response) {
      return {
        code: 'NETWORK_ERROR',
        messageKey: 'errors.network.connection'
      };
    }

    const status = error.response.status;
    const data = error.response.data;

    // Handle specific status codes
    switch (status) {
      case 401:
        // Unauthorized - redirect to login
        setTimeout(() => {
          window.location.href = '/login';
        }, 1000);
        return {
          status,
          code: 'UNAUTHORIZED',
          messageKey: 'errors.auth.required'
        };

      case 402:
        // Payment Required - show credit modal
        if (this.creditModalCallback) {
          this.creditModalCallback();
        }
        return {
          status,
          code: 'INSUFFICIENT_CREDITS',
          messageKey: 'errors.credits.insufficient'
        };

      case 403:
        // Forbidden
        if (data?.error === 'ACCOUNT_EXPIRED') {
          return {
            status,
            code: 'ACCOUNT_EXPIRED',
            messageKey: 'errors.account.expired',
            message: data.message
          };
        }
        return {
          status,
          code: 'FORBIDDEN',
          messageKey: 'errors.access.forbidden',
          message: data?.message
        };

      case 404:
        return {
          status,
          code: 'NOT_FOUND',
          messageKey: 'errors.resource.notFound',
          message: data?.message
        };

      case 409:
        // Conflict
        if (data?.code === 'URL_ALREADY_EXISTS') {
          return {
            status,
            code: 'URL_ALREADY_EXISTS',
            messageKey: 'errors.url.alreadyExists',
            message: data?.message,
            data: data?.existingEntry
          };
        }
        return {
          status,
          code: 'CONFLICT',
          messageKey: 'errors.conflict.generic',
          message: data?.message
        };

      case 429:
        return {
          status,
          code: 'TOO_MANY_REQUESTS',
          messageKey: 'errors.rateLimit.exceeded'
        };

      case 500:
      case 502:
      case 503:
      case 504:
        return {
          status,
          code: 'SERVER_ERROR',
          messageKey: 'errors.server.generic'
        };

      default:
        return {
          status,
          code: 'UNKNOWN_ERROR',
          messageKey: 'errors.unknown',
          message: data?.message || data?.error
        };
    }
  }

  /**
   * Get message key for i18n
   */
  static getMessageKey(error: APIError): string {
    // Return the messageKey if available, otherwise map code to key
    if (error.messageKey) {
      return error.messageKey;
    }
    
    // Map error codes to i18n keys
    const codeToKeyMap: Record<string, string> = {
      'NETWORK_ERROR': 'errors.network.checkConnection',
      'INSUFFICIENT_CREDITS': 'errors.credits.purchaseRequired',
      'ACCOUNT_EXPIRED': 'errors.account.upgradeRequired',
      'URL_ALREADY_EXISTS': 'errors.url.alreadyAnalyzed',
      'UNAUTHORIZED': 'errors.auth.unauthorized',
      'FORBIDDEN': 'errors.access.forbidden',
      'NOT_FOUND': 'errors.resource.notFound',
      'CONFLICT': 'errors.conflict.generic',
      'TOO_MANY_REQUESTS': 'errors.rateLimit.exceeded',
      'SERVER_ERROR': 'errors.server.generic',
      'UNKNOWN_ERROR': 'errors.unknown'
    };
    
    return codeToKeyMap[error.code || ''] || 'errors.unknown';
  }

  /**
   * Check if error is retryable
   */
  static isRetryable(error: APIError): boolean {
    return [
      'NETWORK_ERROR',
      'SERVER_ERROR',
      'TOO_MANY_REQUESTS'
    ].includes(error.code || '');
  }
}

// Export for convenience
export const handleApiError = APIErrorHandler.handleError;
export const getErrorMessageKey = APIErrorHandler.getMessageKey;
export const isRetryableError = APIErrorHandler.isRetryable;